/**
 * Effects over a whole view: turbo speed lines, the slow-motion vignette, flashes (death,
 * level complete). Pure functions of time and state, drawn in screen space; the vignette
 * is a pre-drawn sprite.
 */
import { BOOSTS } from '@cube-rush/shared';
import { createLayer } from './canvas';
import { parseHex, rgba } from './color';
import type { ViewTransform } from './view';

const vignettes = new Map<string, HTMLCanvasElement | null>();

/** Transparent centre fading to `color` at the edges, drawn once per colour. */
function vignette(color: string): HTMLCanvasElement | null {
  let sprite = vignettes.get(color);
  if (sprite === undefined) {
    const layer = createLayer(128, 128);
    if (layer) {
      const [r, g, b] = parseHex(color);
      const gradient = layer.ctx.createRadialGradient(64, 64, 30, 64, 64, 92);
      gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, 0)`);
      gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0.85)`);
      layer.ctx.fillStyle = gradient;
      layer.ctx.fillRect(0, 0, 128, 128);
    }
    sprite = layer?.canvas ?? null;
    vignettes.set(color, sprite);
  }
  return sprite;
}

/** Streaks rushing past while turbo is on. `strength` 0..1 fades them in and out. */
export function drawSpeedLines(
  ctx: CanvasRenderingContext2D,
  view: ViewTransform,
  time: number,
  strength: number,
): void {
  if (strength <= 0) return;
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.55)';
  ctx.lineCap = 'round';
  for (let i = 0; i < 12; i++) {
    const lane = (i * 0.618034 + 0.13) % 1;
    const y = view.y + view.h * (0.08 + lane * 0.84);
    const length = view.w * (0.12 + ((i * 0.37) % 1) * 0.16);
    const speed = view.w * (2.2 + (i % 3) * 0.6);
    const span = view.w + length;
    const x = view.x + view.w - ((time * speed + i * 211) % span);
    ctx.globalAlpha = strength * (0.35 + (i % 4) * 0.12);
    ctx.lineWidth = 1.5 + (i % 3);
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + length, y);
    ctx.stroke();
  }
  ctx.lineCap = 'butt';
  ctx.globalAlpha = 1;
}

/** The purple slow-motion tint and vignette. `strength` 0..1. */
export function drawSlowmo(
  ctx: CanvasRenderingContext2D,
  view: ViewTransform,
  strength: number,
): void {
  if (strength <= 0) return;
  const color = BOOSTS.slowmo.color;
  ctx.fillStyle = rgba(color, 0.1 * strength);
  ctx.fillRect(view.x, view.y, view.w, view.h);
  const sprite = vignette(color);
  if (sprite) {
    ctx.globalAlpha = 0.6 * strength;
    ctx.drawImage(sprite, view.x, view.y, view.w, view.h);
    ctx.globalAlpha = 1;
  }
}

/** A full-view flash that fades out (`amount` 1 → 0). */
export function drawFlash(
  ctx: CanvasRenderingContext2D,
  view: ViewTransform,
  color: string,
  amount: number,
): void {
  if (amount <= 0) return;
  ctx.fillStyle = rgba(color, Math.min(1, amount) * 0.45);
  ctx.fillRect(view.x, view.y, view.w, view.h);
}
